import { useQueryClient } from '@tanstack/react-query';
import Tooltip from 'components/controls/Tooltip';
import FontIcon from 'components/icons/FontIcon';
import ConfirmModal from 'components/modals/ConfirmModal';
import { confirmAlert } from 'components/v2/confirm-dialog/confirmAlert';
import { Form, Formik } from 'formik';
import errorIcon from 'images/icons/svg/error.svg';
import keyIcon from 'images/icons/svg/key.svg';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import { QUERY_KEYS } from 'services/api/constants';
import AuthService from 'services/AuthService';
import { ApiException, MembershipModel } from 'services/EpdClient';
import MembershipService from 'services/MembershipService';
import styled from 'styled-components';
import { ErrorText, HyperLink, SuccessText } from 'styles/Styles.styled';
import { Role } from '../../constants/constants';
import SelectInput from '../form/SelectInput';
import { toaster } from '../v2/toast';
import { RoleOptions, TableRow } from './ManageMembers';

const MemberRow: React.FunctionComponent<{ member: MembershipModel }> = ({ member }) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<boolean>(false);
  const [success, setSuccess] = React.useState<boolean>(false);
  const [isDeleted, setIsDeleted] = React.useState<boolean>(false);
  const { t } = useTranslation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState<boolean>(false);
  const queryClient = useQueryClient();
  const isAdmin = AuthService.isAdmin();
  const isSystemAdmin = AuthService.isSystemAdmin();
  const history = useHistory();

  const changeDeleteModalState = () => {
    if (isAdmin || member.roleId !== Role.EPDVerifier || member.isHomeCompany === false) {
      setIsDeleteModalOpen(!isDeleteModalOpen);
    } else {
      confirmAlert({
        caption: 'You can’t remove the EPD Verifier',
        description: (
          <div>
            If you wish to remove the EPD Verifier from this company, please contact{' '}
            <HyperLink href="mailto:support@environdec.com">support@environdec.com</HyperLink> and our team will assist you.
          </div>
        ),
        acceptLabel: '',
        rejectLabel: 'Close',
        onConfirm: null,
        infoOnly: true,
      });
    }
  };

  const deleteMembership = async (id: string) => {
    try {
      await MembershipService.deleteMembership(id);
      setIsDeleted(true);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] });
    } catch (error: any) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: error?.response?.data?.detail || 'Delete membership operation has not been successful due to an error.',
      });
    }
  };

  const handleRoleChange = async (value: any, member: MembershipModel) => {
    const isCurrentUser = member.userEmail === AuthService.getUser()?.email;
    try {
      setSubmitting(true);
      setSuccess(false);
      setError(false);
      await MembershipService.updateMembership(Object.assign(member, { roleId: value }));
      if (isCurrentUser) {
        await AuthService.refreshUser();

        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.COMPANY_ACCESS_TO_EDIT, member.companyId],
          exact: false,
        });

        if (!AuthService.isCompanyOwner(member.companyId)) {
          history.push(EpdLinks.dashboard());
          return;
        }
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
    setSubmitting(false);
  };

  const handleImpersonate = async (member: MembershipModel) => {
    if (submitting) {
      return;
    }

    const impersonateAs = member.userEmail;
    if (!impersonateAs) {
      return;
    }

    setSubmitting(true);
    try {
      const result = await AuthService.loginAs({ userName: impersonateAs, password: '' });

      history.push(EpdLinks.dashboard());

      toaster({
        severity: 'yellow-exclamation-warning',
        summary: 'Impersonate mode has been enabled',
        details: (
          <span>
            You are currently logged in as <a href={`mailto:${result.email}`}>{result.email}</a>.
            <br />
            <br />
            Please log in again with your credentials to access the EPD Portal as a Super Admin.
          </span>
        ),
      });
    } catch (e) {
      if (e instanceof Error) {
        let message = e.message;
        if (e instanceof ApiException) {
          const response = JSON.parse(e.response);
          if (response.detail) {
            message = response.detail;
          }
        }
        toaster({
          severity: 'error',
          summary: 'Failed to impersonate',
          details: message,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (isDeleted) {
    return null;
  }

  return (
    <Formik initialValues={{ roleId: member.roleId, roleName: member.roleName }} onSubmit={() => {}}>
      {({ isSubmitting, errors, handleChange }) => (
        <Form>
          <TableRow key={member.id} isSubmitting={submitting}>
            <div style={{ flexGrow: 1 }}>
              {member.userName}
              {member.userName && member.userName !== member.userEmail && ` (${member.userEmail})`}
              {member.isAccountManager && (
                <>
                  <img
                    id="accountManagerIcon"
                    alt="User is account manager"
                    src={keyIcon}
                    style={{ height: '15px', margin: '0 1rem' }}
                  />
                  <Tooltip targetId="accountManagerIcon" position="title">
                    {t('manageMembers.userIsAccountMng')}
                  </Tooltip>
                </>
              )}
              {error && <ErrorText style={{ display: 'inline' }}> {t('manageMembers.errors.error')}</ErrorText>}
              {success && <SuccessText style={{ display: 'inline' }}> {t('manageMembers.messages.saved')}</SuccessText>}
            </div>
            {isSystemAdmin && (
              <ImpersonateButton
                type="button"
                title="Impersonate"
                aria-label="Impersonate"
                onClick={() => handleImpersonate(member)}
                disabled={submitting}
              >
                <FontIcon icon="users" />
              </ImpersonateButton>
            )}
            <SelectInput
              style={{ margin: 0 }}
              disabled={member.isAccountManager || member.roleId === Role.EPDVerifier}
              name="roleId"
              onChange={(e: any) => {
                handleChange(e);
                handleRoleChange(e.target.value, member);
              }}
            >
              <RoleOptions />
            </SelectInput>

            <DeleteIcon
              style={{ visibility: member.isAccountManager ? 'hidden' : 'visible' }}
              src={errorIcon}
              onClick={() => changeDeleteModalState()}
            />
            {isDeleteModalOpen && (
              <ConfirmModal
                description={t('confirmModal.deleteMember')}
                confirmButtonText={t('delete')}
                cancelButtonText={t('cancel')}
                onClickConfirm={() => {
                  deleteMembership(member.id);
                  changeDeleteModalState();
                }}
                onClickCancel={() => changeDeleteModalState()}
              />
            )}
          </TableRow>
        </Form>
      )}
    </Formik>
  );
};

const DeleteIcon = styled.img`
  cursor: pointer;
  height: 15px;
  margin: 0 1rem;
`;

const ImpersonateButton = styled.button`
  cursor: pointer;
  border: none;
  background: transparent;
  margin: 0 1rem;
  padding: 0;

  i {
    font-size: 16px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export default MemberRow;
